import React, { useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, FileSpreadsheet, Info } from 'lucide-react';
import {
  columnDisplayOrder,
  columnSample,
  normalizeHeader,
  sellPriceExample,
  sellPriceModes,
} from './productImport';

const money = (amount) => `$${amount.toFixed(2)}`;

export default function ImportMapStep({
  itemType,
  fileName,
  headers,
  dataRows,
  mapping,
  fields,
  onChangeField,
  sellPriceMode,
  onChangeSellPriceMode,
  onBack,
  onContinue,
}) {
  const noun = itemType === 'service' ? 'service' : 'product';
  const mapped = new Set(mapping.filter(Boolean));
  // Worked out once when the step opens so rows don't jump while fields are changed.
  const [columnOrder] = useState(() => columnDisplayOrder(mapping));
  const fieldLabel = (key) => fields.find((f) => f.key === key)?.label || '';
  const modes = sellPriceModes(mapping);
  const sellPriceCol = mapping.indexOf('sellingPrice');
  const markupCol = mapping.indexOf('markup');
  const sellHeader = headers[sellPriceCol];
  const priceHeader = headers[mapping.indexOf('price')];
  const markupHeader = headers[markupCol];
  const hasTotalColumn = headers.some((h) => normalizeHeader(h).includes('total'));
  const example = markupCol !== -1 ? sellPriceExample(dataRows, mapping, sellPriceMode) : null;
  const worked = example
    ? ` Row ${example.rowNumber}: ${money(example.unit)} + ${Number(
        example.markup.toFixed(2)
      )}% = ${money(example.total)}.`
    : '';
  const modeLabels = {
    withMarkup: `Add ${markupHeader} to ${sellHeader}`,
    tradeMarkup: `Add ${markupHeader} to ${priceHeader}`,
    file: `Use ${sellHeader} as exported`,
    none: "Don't import selling prices",
  };

  let sellPriceHelp =
    'Proposals will price these items from the trade price and the project markup.';
  if (sellPriceMode === 'withMarkup') {
    sellPriceHelp = `${sellHeader} is before markup in tracker exports, so adding ${markupHeader} gives what the client was charged.${worked}`;
  } else if (sellPriceMode === 'tradeMarkup') {
    sellPriceHelp = `${priceHeader} is the cost, so adding ${markupHeader} gives the selling price.${worked} Items without a markup are priced from the project markup.`;
  } else if (sellPriceMode === 'file') {
    sellPriceHelp = `Review markup separately before saving.${
      hasTotalColumn ? ' Total Selling Price will not replace the per-unit price.' : ''
    }`;
  }

  return (
    <>
      <div className="pi-card pi-file-chip">
        <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
        <span>{fileName}</span>
      </div>

      <section className="pi-card pi-map-card">
        <div className="pi-table-wrap">
          <table className="pi-table pi-map-table">
            <thead>
              <tr>
                <th>Excel column</th>
                <th>Sample from Excel</th>
                <th>InteriorFolio field</th>
              </tr>
            </thead>
            <tbody>
              {columnOrder.map((col) => {
                const header = headers[col];
                const field = mapping[col];
                const sample = columnSample(dataRows, col, field);
                return (
                  <tr key={`${header}-${col}`}>
                    <td className="pi-map-header">{header}</td>
                    <td className={sample ? 'pi-map-sample' : 'pi-map-sample is-empty'}>
                      {sample || 'Empty'}
                    </td>
                    <td>
                      <select
                        id={`pi-map-${col}`}
                        className={field ? 'is-mapped' : ''}
                        value={field}
                        onChange={(e) => onChangeField(col, e.target.value)}
                        aria-label={`InteriorFolio field for ${header}`}
                      >
                        <option value="">Don't import</option>
                        {fields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!mapped.has('name') && (
          <div className="pi-banner pi-banner--error" role="alert">
            <AlertTriangle size={18} aria-hidden="true" />
            Choose which column holds the {fieldLabel('name').toLowerCase()}.
          </div>
        )}
        {!mapped.has('salesCode') && (
          <div className="pi-banner pi-banner--warning">
            <AlertTriangle size={18} aria-hidden="true" />
            Sales Code is not included in this file. Choose a sales code for each {noun} during
            review.
          </div>
        )}
        {!mapped.has('price') && (
          <div className="pi-banner pi-banner--warning">
            <AlertTriangle size={18} aria-hidden="true" />
            {fieldLabel('price')} is not included in this file. Enter a trade price for each {noun}{' '}
            during review.
          </div>
        )}
        {mapped.has('imageUrl') && (
          <div className="pi-banner pi-banner--info">
            <Info size={18} aria-hidden="true" />
            Image links are saved as they are. Houzz image links stop working after a week, so
            upload the image files on the review step when you can.
          </div>
        )}
        {mapped.has('customSpec') && (
          <div className="pi-banner pi-banner--info">
            <Info size={18} aria-hidden="true" />
            Custom Spec columns become client-facing specs on each {noun}, labelled with the column
            name. The studio can hide them per {noun}.
          </div>
        )}
        {mapped.has('archived') && (
          <div className="pi-banner pi-banner--info">
            <Info size={18} aria-hidden="true" />
            Archived {noun}s are listed on the review step but left unselected.
          </div>
        )}
      </section>

      {modes.length > 0 && (
        <section className="pi-card pi-sell-price-card">
          <label className="pi-field" htmlFor="pi-sell-price-mode">
            <span className="pi-field-label">How should selling prices be imported?</span>
          </label>
          <select
            id="pi-sell-price-mode"
            value={sellPriceMode}
            onChange={(e) => onChangeSellPriceMode(e.target.value)}
          >
            {modes.map((mode) => (
              <option key={mode} value={mode}>
                {modeLabels[mode]}
              </option>
            ))}
          </select>
          <p className="pi-help">{sellPriceHelp}</p>
        </section>
      )}

      <div className="pi-footer">
        <button type="button" className="pi-btn pi-btn--outline" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> Back to Upload
        </button>
        <button
          type="button"
          className="pi-btn pi-btn--primary pi-btn--lg"
          onClick={onContinue}
          disabled={!mapped.has('name')}
        >
          Continue to Review {itemType === 'service' ? 'Services' : 'Products'}{' '}
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    </>
  );
}
